import React from 'react';
import { Modal, Select, Table, Button } from 'antd';
import styled from 'styled-components';
import { useMutation, useQuery } from '@apollo/client';
import { PendingHubspotDeal, PendingHubspotDealsResponse } from '../../../graphql/query/pendingHubspotDeals';
import { RESOLVE_HUBSPOT_DEAL } from '../../../graphql/mutations/resolveHubspotDeal';
import { DISMISS_HUBSPOT_DEAL } from '../../../graphql/mutations/dismissHubspotDeal';
import { ORGANIZATION_LIST, OrganizationItem } from '../../../graphql/query/organizationList';
import { snackbar } from '../../../components/ui/Snackbar';
import { displayGqlErrors } from '../../../components/ui/ErrorList';
import * as Sentry from '@sentry/react';
import { createSortProps } from '../../../utils/table.utils';

const { Column } = Table;
const { confirm } = Modal;

const PageContent = styled.div`
  padding: 10px 10px;
  width: 100%;
  align-items: flex-start;
`;

const PageHeader = styled.div`
  width: 100%;
  height: 56px;
  padding: 12px 16px;
  border-bottom: 1px solid ${(props) => props.theme.colors.dimGray.plus3};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

interface PendingLocationsColumnProps {
  pendingDealsData?: PendingHubspotDealsResponse;
  refetchPendingDeals: () => void;
}

export const PendingLocationsColumn: React.FC<PendingLocationsColumnProps> = ({
  pendingDealsData,
  refetchPendingDeals,
}) => {
  const { data: organizationsData } = useQuery(ORGANIZATION_LIST, {
    variables: { onlyActive: true },
  });

  const [resolveHubspotDeal] = useMutation(RESOLVE_HUBSPOT_DEAL);
  const [dismissHubspotDeal] = useMutation(DISMISS_HUBSPOT_DEAL);

  const handleOrganizationSelect = (dealId: string, organizationId: string | null) => {
    confirm({
      title: 'Confirm Organization Selection',
      content: organizationId
        ? 'Are you sure you want to associate this deal with the selected organization?'
        : 'Are you sure you want to create a new organization for this deal?',
      onOk: async () => {
        try {
          const response = await resolveHubspotDeal({
            variables: {
              id: dealId,
              organizationId: organizationId || undefined,
            },
          });

          const newOrganizationId = response.data?.resolveHubspotDeal.organizationId;
          if (newOrganizationId) {
            window.open(`/app/organization/${newOrganizationId}`, '_blank');
          }

          snackbar.success({ message: 'Deal resolved successfully!' });
          refetchPendingDeals();
        } catch (error) {
          Sentry.captureException(error);
          displayGqlErrors(error, 'Unable to resolve deal');
        }
      },
    });
  };

  const handleDismiss = (dealId: string) => {
    confirm({
      title: 'Dismiss Deal',
      content:
        'Are you sure you want to dismiss this deal? This will remove it from the pending deals list without creating a location.',
      onOk: async () => {
        try {
          await dismissHubspotDeal({
            variables: {
              id: dealId,
            },
          });

          snackbar.success({ message: 'Deal dismissed successfully!' });
          refetchPendingDeals();
        } catch (error) {
          Sentry.captureException(error);
          displayGqlErrors(error, 'Unable to dismiss deal');
        }
      },
    });
  };

  const organizationOptions = [
    { label: 'Create New Organization', value: null },
    ...(organizationsData?.organizations.map((org: OrganizationItem) => ({
      label: org.name,
      value: org.id,
    })) || []),
  ];

  return (
    <>
      <PageHeader>
        <div>Pending Locations</div>
      </PageHeader>
      <PageContent>
        <Table
          pagination={{ pageSize: 50 }}
          className="wide-table"
          dataSource={pendingDealsData?.pendingHubspotDeals || []}
          rowKey="id"
        >
          <Column
            title="Deal Name"
            dataIndex="dealName"
            key="dealName"
            {...createSortProps({
              type: 'string',
              field: 'dealName',
              multiple: 1,
            })}
          />
          <Column
            title="Organization"
            key="organization"
            render={(_, record: PendingHubspotDeal) => (
              <Select
                style={{ width: 200 }}
                placeholder="Select organization"
                options={organizationOptions}
                onChange={(value) => handleOrganizationSelect(record.id, value)}
                showSearch
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              />
            )}
          />
          <Column
            title="Actions"
            key="actions"
            render={(_, record: PendingHubspotDeal) => (
              <Button type="link" danger onClick={() => handleDismiss(record.id)}>
                Dismiss
              </Button>
            )}
          />
        </Table>
      </PageContent>
    </>
  );
};
